import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import { base64ToArrayBuffer } from './base64Helpers';
import { FileHelperLogger } from '../logger';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { arrayBufferToBase64 } from './base64Helpers';

/**
 * Verifica se um arquivo local existe
 * @param uri URI do arquivo local
 * @returns Promise com um boolean indicando se o arquivo existe
 */
export async function fileExistsAsync(uri: string): Promise<boolean> {
  try {
    if (!uri) return false;
    
    // Na web, verificar na tabela CHECKLIST_PHOTOS ou CHECKLIST_SIGNATURES se for imagem de checklist
    if (Platform.OS === 'web') {
      // Considera imagens de checklist se o uri for do tipo 'checklist-photos' ou temp
      if (uri.startsWith('checklist-photos') || uri.startsWith('checklist-signatures') || uri.startsWith('checklist-marcacoes') || uri.startsWith('blob:') || uri.startsWith('temp://')) {
        const row = await databaseService.readItem(
          uri.startsWith('checklist-photos') ? APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME : uri.startsWith('checklist-signatures') ? APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME : APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME,
          uri
        );
        return !!row;
      }
      // Para outros casos, manter false
      return false;
    }
    
    const info = await FileSystem.getInfoAsync(uri);
    return info.exists;
  } catch {
    return false;
  }
}

/**
 * Converte um URI local para um blob ou ArrayBuffer para upload
 * @param uri URI do arquivo local
 * @returns Promise com o Blob (web) ou ArrayBuffer (mobile)
 */
export async function fetchFile(uri: string): Promise<Blob | ArrayBuffer> {
  if (Platform.OS === 'web') {
    // Se for id de checklist-photos, buscar na Dexie
    if (uri.startsWith('checklist-photos/') || uri.startsWith('checklist-signatures/') || uri.startsWith('checklist-marcacoes/')) {
      const row = await databaseService.readItem<{ base64: string }>(
        uri.startsWith('checklist-photos') ? APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME : uri.startsWith('checklist-signatures') ? APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME : APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME,
        uri
      );
      if (!row || !row.base64) throw new Error('Imagem não encontrada no IndexedDB');
      // Detectar tipo da imagem pelo prefixo base64 (se possível)
      let mimeType = 'image/jpeg';
      if (row.base64.startsWith('iVBORw0KGgo')) {
        mimeType = 'image/png';
      } else if (row.base64.startsWith('R0lGOD')) {
        mimeType = 'image/gif';
      } else if (row.base64.startsWith('UklGR')) {
        mimeType = 'image/webp';
      }
      // Converter base64 para ArrayBuffer e criar Blob
      const byteArray = base64ToArrayBuffer(row.base64);
      return new Blob([byteArray], { type: mimeType });
    }
    // Se for blob: ou data:, usar fetch normalmente
    const response = await fetch(uri);
    return await response.blob();
  }
  // Para mobile, usar o Expo FileSystem
  const fileInfo = await FileSystem.getInfoAsync(uri);
  if (!fileInfo.exists) {
    throw new Error(`Arquivo não encontrado: ${uri}`);
  }
  // Ler o arquivo como base64
  const base64 = await FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.Base64 });
  // Converter base64 para ArrayBuffer
  return base64ToArrayBuffer(base64);
}

/**
 * Exclui um arquivo local
 * @param uri URI do arquivo local
 * @returns Promise<void>
 */
export async function deleteLocalFile(uri: string): Promise<void> {
  FileHelperLogger.DeleteLocalFile.info(`excluindo o arquivo local: ${uri}`);
  if (!uri) return;
  // Para web, deletar da tabela CHECKLIST_PHOTOS se for imagem de checklist
  if (Platform.OS === 'web') {
    if (uri.startsWith('checklist-photos') || uri.startsWith('checklist-signatures') || uri.startsWith('checklist-marcacoes') || uri.startsWith('blob:') || uri.startsWith('temp://')) {
      await databaseService.deleteItem(
        uri.startsWith('checklist-photos') ? APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME : uri.startsWith('checklist-signatures') ? APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME : APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME,
        uri,
        true // skipPendingOperation = true
      );
      FileHelperLogger.DeleteLocalFile.info(`Imagem de checklist deletada da tabela ${uri.startsWith('checklist-photos') ? 'CHECKLIST_PHOTOS' : uri.startsWith('checklist-signatures') ? 'CHECKLIST_SIGNATURES' : 'CHECKLIST_MARCACOES'}: ${uri}`);
      return;
    }
    FileHelperLogger.DeleteLocalFile.info(`não há necessidade de excluir o arquivo local: ${uri} no web`);
    return;
  }
  
  // Para mobile, excluir usando Expo FileSystem
  try {
    FileHelperLogger.DeleteLocalFile.info(`excluindo o arquivo local: ${uri} no mobile`);
    const fileInfo = await FileSystem.getInfoAsync(uri);
    if (fileInfo.exists) {
      await FileSystem.deleteAsync(uri, { idempotent: true });
    }
    FileHelperLogger.DeleteLocalFile.info(`arquivo local excluído com sucesso: ${uri}`);
  } catch (error) {
    FileHelperLogger.DeleteLocalFile.error(`erro ao excluir arquivo local: ${uri}`, error);
  }
}

/**
 * Gera um URI temporário para uma imagem local
 * @param prefixo Prefixo para o caminho do arquivo (ex: 'user-avatar')
 * @returns URI temporário para o arquivo
 */
export function getTempFileUri(prefixo: string): string {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  
  if (Platform.OS === 'web') {
    // Para web, não precisamos criar um arquivo físico, apenas um URI temporário
    return `temp://${prefixo}/${timestamp}-${randomString}`;
  } else {
    // Para mobile, retornar um caminho no sistema de arquivos temporário
    return `${FileSystem.cacheDirectory}${prefixo}-${timestamp}-${randomString}.jpg`;
  }
}

/**
 * Salva um arquivo localmente (apenas mobile)
 * @param uri URI do arquivo a ser salvo
 * @param destination Caminho de destino
 * @returns URI do arquivo salvo
 */
export async function saveFileLocally(uri: string, destination: string): Promise<string> {
  if (Platform.OS === 'web') {
    // Para web, apenas retornar o URI original (não precisamos copiar)
    return uri;
  }
  
  // Para mobile, copiar o arquivo para o destino
  await FileSystem.copyAsync({
    from: uri,
    to: destination
  });
  
  return destination;
}

/**
 * Cria um diretório local se não existir (apenas mobile)
 * @param directory Caminho do diretório
 */
export async function ensureDirectoryExists(directory: string): Promise<void> {
  if (Platform.OS === 'web') return;
  
  const dirInfo = await FileSystem.getInfoAsync(directory);
  if (!dirInfo.exists) {
    await FileSystem.makeDirectoryAsync(directory, { intermediates: true });
  }
} 